/**
 * Shared generation-candidate helpers — the poll hook, result-byte fetch, object-URL
 * hook, and status → badge/label mappers used by the single-design GeneratePanel.
 * Hoisted here so there is ONE copy of the polling/status logic.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { GenerationJobView, JsonClient, PlatformIO } from "@genz/platform";
import { fetchBytesWithRetry } from "../templates/bakedSync.js";
import { errorDetail } from "./errorCode.js";
import { jobGone } from "./jobActions.js";
import { fetchWithTimeout, DOWNLOAD_TIMEOUT_MS, NO_HTTP_CACHE } from "../templates/timeout.js";

/** How often the job poll refreshes in-flight jobs. */
export const POLL_INTERVAL_MS = 2500;
/** Jobs asked for in one tick, the longest unasked first: the cloud reads 600 a minute. */
export const MAX_POLLS_PER_TICK = 8;

/** A held job waits on the cloud's recheck, and a clear image in review on a person: ask about
 *  either once a minute. */
export const HELD_POLL_MS = 60_000;
const SLOW_STATUSES: ReadonlySet<GenerationJobView["status"]> = new Set(["held", "clear_review"]);

/** Consecutive ticks where EVERY outstanding job errored (server down / bad ids) before
 *  the poll gives up and surfaces an error. ~5 × 2.5s ≈ 12s of dead polling, then stop
 *  rather than hammer a dead endpoint forever with no UI signal. Resumable via `retry`. */
export const MAX_DEAD_POLL_TICKS = 5;

/** Statuses that mean the job is done (no more polling). Not `held`: the cloud's recheck
 *  may still finish it, and this screen must show the image when it does. */
export const TERMINAL_STATUSES: ReadonlySet<GenerationJobView["status"]> = new Set([
  "completed",
  "failed",
  "needs_review",
  "approved",
  "cancelled",
]);

export const isTerminal = (j: GenerationJobView): boolean => TERMINAL_STATUSES.has(j.status);

/** A job a person can still stop: queued, running, or waiting on its clear-image check. */
export const isOngoing = (j: GenerationJobView): boolean =>
  j.status === "queued" || j.status === "processing" || j.status === "clear_review";

/** A job whose result image is fetchable (completed/review/approved WITH a result url). */
export const isReady = (j: GenerationJobView): boolean =>
  (j.status === "completed" || j.status === "needs_review" || j.status === "approved") &&
  !!j.resultUrl;

/** A job with an image to look at: a clear image waiting for review, or a ready result. */
export const hasImage = (j: GenerationJobView): boolean =>
  (j.status === "clear_review" && !!j.clearUrl) || isReady(j);

/** Aurora badge variant for a job status — status colors are MEANINGFUL. The
 *  `inFlight` flag marks Queued / Generating… rows, which show a running WorkingTimer. */
export function statusBadge(status: GenerationJobView["status"]): {
  variant: "success" | "info" | "danger" | "neutral";
  inFlight: boolean;
} {
  switch (status) {
    case "queued":
      return { variant: "neutral", inFlight: true };
    case "processing":
      return { variant: "info", inFlight: true };
    case "failed":
    case "held":
      return { variant: "danger", inFlight: false };
    case "completed":
    case "approved":
      return { variant: "success", inFlight: false };
    case "needs_review":
    case "clear_review":
      return { variant: "info", inFlight: false };
    default:
      return { variant: "neutral", inFlight: false };
  }
}

/** Human label for a job's lifecycle status (shown in the per-view list). */
export function statusLabel(status: GenerationJobView["status"]): string {
  switch (status) {
    case "queued":
      return "Queued";
    case "processing":
      return "Generating…";
    case "failed":
      return "Failed";
    case "held":
      return "On hold";
    case "completed":
    case "approved":
      return "Done";
    case "needs_review":
      return "Ready (in review)";
    case "clear_review":
      return "Check the clear image";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

/** A two-pass job goes back to `queued` between its passes: it is still generating. */
export const inPassTwo = (j: GenerationJobView): boolean =>
  j.pass === "magenta" && (j.status === "queued" || j.status === "processing");

/** The cloud's notes for a print guide it could not make: the result is the clear image, unkeyable. */
const NO_GUIDE_NOTES = ["clear_restore_skipped", "coverage_check_failed"];

/** A finished print job whose result has no print guide: it cannot go into a template. */
export const guideMissing = (j: GenerationJobView): boolean =>
  isReady(j) && !j.mockupOnly && !!j.checkNotes?.some((n) => NO_GUIDE_NOTES.some((p) => n.startsWith(p)));

/** `statusBadge` for a whole job, so the second pass reads as generating. */
export const jobBadge = (j: GenerationJobView) =>
  guideMissing(j) ? statusBadge("failed") : statusBadge(inPassTwo(j) ? "processing" : j.status);

/** `statusLabel` for a whole job, naming the second pass. */
export const jobLabel = (j: GenerationJobView): string =>
  guideMissing(j) ? "Print guide failed" : inPassTwo(j) ? "Generating (pass 2)" : statusLabel(j.status);

/** False while the window is hidden (another app or tab in front). */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() => typeof document === "undefined" || !document.hidden);
  useEffect(() => {
    const onChange = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return visible;
}

/**
 * Poll the given job ids until all are terminal. Returns the latest snapshot per id
 * and a `polling` flag. Clears its interval on unmount and once every job is done.
 * `paused` stops the loop and keeps the snapshots; a held job is asked about every
 * HELD_POLL_MS rather than every tick. A job the cloud no longer has (404: removed elsewhere)
 * is not asked about again; `onGone` tells the caller to drop its row.
 */
export function useGenerationJobs(
  client: JsonClient,
  ids: string[],
  { paused = false, onGone }: { paused?: boolean; onGone?: (id: string) => void } = {},
): {
  snapshots: Record<string, GenerationJobView>;
  polling: boolean;
  /** Set once polling gives up after MAX_DEAD_POLL_TICKS consecutive all-error ticks. */
  error: string | null;
  /** Clear the error and resume polling (wire to a "Retry" button). */
  retry: () => void;
} {
  const [snapshots, setSnapshots] = useState<Record<string, GenerationJobView>>({});
  const [error, setError] = useState<string | null>(null);
  // Bumping this restarts the effect (the manual retry after a give-up).
  const [retryNonce, setRetryNonce] = useState(0);
  // Stable key so the effect only restarts when the SET of ids changes.
  const idsKey = ids.join("|");
  const gone = useRef(onGone);
  gone.current = onGone;

  useEffect(() => {
    if (ids.length === 0) {
      setSnapshots({});
      setError(null);
      return;
    }
    if (paused) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    setError(null);

    const pending = new Set(ids);
    // Count CONSECUTIVE ticks where every outstanding poll failed. A single success (any
    // job) resets it — a transient blip never escalates, but a sustained outage stops the
    // loop and tells the user, instead of silently polling a dead endpoint forever.
    let consecutiveDeadTicks = 0;
    const heldAskedAt = new Map<string, number>();
    const due = (id: string) => Date.now() - (heldAskedAt.get(id) ?? -Infinity) >= HELD_POLL_MS;
    const askedAt = new Map<string, number>();
    let ticking = false;

    const tick = async () => {
      if (ticking) return;
      const targets = [...pending].filter(due)
        .sort((a, b) => (askedAt.get(a) ?? -1) - (askedAt.get(b) ?? -1))
        .slice(0, MAX_POLLS_PER_TICK);
      if (targets.length === 0) return;
      ticking = true;
      for (const [k, id] of targets.entries()) askedAt.set(id, Date.now() + k);
      const ok = await Promise.all(
        targets.map(async (id) => {
          try {
            const job = await client.getGenerationJob(id);
            if (cancelled) return true;
            setSnapshots((prev) => ({ ...prev, [id]: job }));
            if (isTerminal(job)) pending.delete(id);
            if (SLOW_STATUSES.has(job.status)) heldAskedAt.set(id, Date.now());
            else heldAskedAt.delete(id);
            return true;
          } catch (e) {
            if (!jobGone(e)) return false; // transient — the next tick retries (unless it stays dead)
            // The server answered: the job is gone, which is neither an outage nor a retry.
            pending.delete(id);
            if (cancelled) return true;
            setSnapshots(({ [id]: _gone, ...rest }) => rest);
            gone.current?.(id);
            return true;
          }
        }),
      );
      ticking = false;
      if (cancelled) return;
      consecutiveDeadTicks = ok.some(Boolean) ? 0 : consecutiveDeadTicks + 1;
      if (consecutiveDeadTicks >= MAX_DEAD_POLL_TICKS) {
        setError("Couldn't reach the server to check generation status. Retry when you're back online.");
        if (timer) {
          clearInterval(timer);
          timer = undefined;
        }
        return;
      }
      if (pending.size === 0 && timer) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    // Poll immediately, then on an interval until everything is terminal.
    void tick();
    timer = setInterval(() => void tick(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // idsKey captures the id set; client is stable from context; retryNonce forces a resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, client, retryNonce, paused]);

  const retry = useCallback(() => {
    setError(null);
    setRetryNonce((n) => n + 1);
  }, []);

  // While errored we are NOT polling (the loop stopped); otherwise poll until all terminal.
  const polling =
    !paused &&
    error === null &&
    ids.some((id) => {
      const j = snapshots[id];
      return !j || !isTerminal(j);
    });
  return { snapshots, polling, error, retry };
}

/** Decode a `data:<mediaType>;base64,...` data-URI into raw bytes + mediaType. */
export function decodeDataUri(dataUri: string): { bytes: Uint8Array; mediaType: string } {
  const match = /^data:([^;]+);base64,(.*)$/s.exec(dataUri);
  if (!match) throw new Error("Generated image was not a base64 data-URI.");
  const [, mediaType, b64] = match;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return { bytes, mediaType };
}

/**
 * The image format the BYTES actually are, or `undefined` when they are not one this app
 * recognises (WS-HF F5).
 *
 * The media type is not cosmetic here: it sets the multipart part type on
 * `submitDeviceResult` (as `media_type`), it picks the engine's decoder, and it becomes the export filename's
 * extension. A signed result URL is served by a CDN, and a CDN very often answers
 * `application/octet-stream` — or, worse, confidently answers the wrong type. The magic bytes
 * are the only authority that is always right, so they win; the header is the fallback.
 *
 * Only the three formats a generation provider actually returns are sniffed. Anything else
 * (AVIF, HEIC, a future format) falls through to the header on purpose: guessing wider than
 * we can verify is how a wrong answer gets a confident label.
 */
export function sniffImageMediaType(bytes: Uint8Array): string | undefined {
  if (bytes.length < 12) return undefined;
  const at = (i: number) => bytes[i];
  // PNG — 89 50 4E 47 0D 0A 1A 0A
  if (at(0) === 0x89 && at(1) === 0x50 && at(2) === 0x4e && at(3) === 0x47
    && at(4) === 0x0d && at(5) === 0x0a && at(6) === 0x1a && at(7) === 0x0a) return "image/png";
  // JPEG — FF D8 FF
  if (at(0) === 0xff && at(1) === 0xd8 && at(2) === 0xff) return "image/jpeg";
  // WebP — "RIFF" ???? "WEBP". The "RIFF" alone is also a WAV and an AVI, so BOTH four-byte
  // tags are required.
  if (at(0) === 0x52 && at(1) === 0x49 && at(2) === 0x46 && at(3) === 0x46
    && at(8) === 0x57 && at(9) === 0x45 && at(10) === 0x42 && at(11) === 0x50) return "image/webp";
  return undefined;
}

/** Bytes → a `data:` URI. Chunked: spreading a multi-MB image blows the argument limit. */
export function toDataUri(bytes: Uint8Array, mediaType: string): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return `data:${mediaType};base64,${btoa(binary)}`;
}

/**
 * Fetch a completed result URL into raw bytes + media type for on-device masking.
 *
 * `downloadBytes` (desktop only) takes the request out of the web view: its CSP and its
 * connection pool have both failed a result download with a bare `Load failed` after the
 * vendor had already charged for the image.
 */
export async function fetchResultBytes(
  url: string,
  downloadBytes?: PlatformIO["downloadBytes"],
): Promise<{ bytes: Uint8Array; mediaType: string }> {
  if (url.startsWith("data:")) return decodeDataUri(url);
  const { bytes, declared } = downloadBytes
    ? { bytes: await fetchBytesWithRetry(url, downloadBytes), declared: undefined }
    : await fetchOverWebView(url);
  const mediaType = sniffImageMediaType(bytes) ?? (declared || "image/png");
  return { bytes, mediaType };
}

async function fetchOverWebView(url: string): Promise<{ bytes: Uint8Array; declared?: string }> {
  const res = await fetchWithTimeout(url, NO_HTTP_CACHE, DOWNLOAD_TIMEOUT_MS, "Generated image download").catch(
    (e: unknown) => {
      throw new Error(`Could not download the generated image from ${hostOf(url)} (${errorDetail(e)}).`);
    },
  );
  if (!res.ok) throw new Error(`Could not load the generated image (${res.status}).`);
  const bytes = new Uint8Array(await res.arrayBuffer());
  // `; charset=…` and friends are not part of the type the rest of the app compares against.
  return { bytes, declared: res.headers.get("content-type")?.split(";")[0].trim() };
}

/** The url's host, or the url itself when it has none to parse (a relative link). */
function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** A revocable object URL for an in-memory image's bytes (thumbnail / preview). Used so
 *  the editor displays from already-held pixels, never an expiring signed CDN URL. Made and
 *  revoked in ONE effect: a URL made during render is revoked by StrictMode's re-run. */
export function useObjectUrl(file?: { bytes: Uint8Array; mime?: string }): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!file || typeof URL === "undefined" || !URL.createObjectURL) {
      setUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(new Blob([file.bytes as BlobPart], { type: file.mime ?? "image/png" }));
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}
